import { useState, useCallback } from 'react';
import { message } from 'antd';
import {
  j203Api, FJ130, FJ131, FJ133, FG031,
  J203CreateRequest,
} from '../../../services/j203Api';
import { useAuth } from '../../../contexts/AuthContext';
import { Mode } from './useJ203Mode';

export interface J203DetailTables {
  details: Partial<FJ131>[];
  extras: Partial<FJ133>[];
  payments: Partial<FG031>[];
}

const emptyDetails = (): J203DetailTables => ({
  details: [],
  extras: [],
  payments: [],
});

export function useJ203Record() {
  const { user } = useAuth();
  const [record, setRecord] = useState<FJ130 | null>(null);
  const [formData, setFormData] = useState<Partial<FJ130>>({});
  const [detailTables, setDetailTables] = useState<J203DetailTables>(emptyDetails());
  const [loading, setLoading] = useState(false);

  /** Load a single record by ID */
  const loadRecord = useCallback(async (id: string): Promise<FJ130 | null> => {
    setLoading(true);
    try {
      const data = await j203Api.get(id);
      setRecord(data);
      setFormData({ ...data });
      setDetailTables({
        details: data.details || [],
        extras: data.extras || [],
        payments: data.payments || [],
      });
      return data;
    } catch {
      message.error('載入失敗');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  /** Set a single master field */
  const setField = useCallback((key: keyof FJ130, value: FJ130[keyof FJ130]) => {
    setFormData(prev => ({ ...prev, [key]: value }));
  }, []);

  /** Get a single master field value */
  const getField = useCallback((key: keyof FJ130): FJ130[keyof FJ130] => {
    return (formData as Record<string, FJ130[keyof FJ130]>)[key] ?? '';
  }, [formData]);

  /** Update a specific detail table */
  const setDetailTable = useCallback(<K extends keyof J203DetailTables>(
    tableKey: K,
    updater: (prev: J203DetailTables[K]) => J203DetailTables[K],
  ) => {
    setDetailTables(prev => ({ ...prev, [tableKey]: updater(prev[tableKey]) }));
  }, []);

  /** Build request payload */
  const buildRequest = useCallback((): J203CreateRequest => ({
    master: formData as FJ130,
    details: detailTables.details as FJ131[],
    extras: detailTables.extras as FJ133[],
    payments: detailTables.payments as FG031[],
  }), [formData, detailTables]);

  /** Save (create or update) */
  const handleSave = useCallback(async (
    mode: Mode,
    onSuccess: (result: FJ130) => void,
  ) => {
    setLoading(true);
    try {
      const req = buildRequest();
      if (mode === 'add') {
        const result = await j203Api.create(req);
        message.success(`新增成功: ${result.fj130_01}`);
        onSuccess(result);
      } else {
        const result = await j203Api.update(formData.fj130_01!, req);
        message.success('修改成功');
        applyResult(result);
        onSuccess(result);
      }
    } catch (e: unknown) {
      const err = e as { response?: { data?: { errors?: { message: string }[]; error?: string } } };
      const errors = err.response?.data?.errors;
      if (errors && Array.isArray(errors)) {
        message.error(errors.map((er: { message: string }) => er.message).join('; '));
      } else {
        message.error(err.response?.data?.error || '儲存失敗');
      }
    } finally {
      setLoading(false);
    }
  }, [formData, buildRequest]);

  /** Delete the current record */
  const handleDelete = useCallback(async (): Promise<boolean> => {
    if (!record) return false;
    try {
      await j203Api.delete(record.fj130_01);
      message.success('刪除成功');
      return true;
    } catch {
      message.error('刪除失敗');
      return false;
    }
  }, [record]);

  /** Apply loaded/saved result */
  const applyResult = useCallback((result: FJ130) => {
    setRecord(result);
    setFormData({ ...result });
    setDetailTables({
      details: result.details || [],
      extras: result.extras || [],
      payments: result.payments || [],
    });
  }, []);

  /** Reset form to current record (revert) */
  const revertToRecord = useCallback(() => {
    if (record) applyResult(record);
  }, [record, applyResult]);

  /** Reset form for a new add */
  const resetForAdd = useCallback(() => {
    setRecord(null);
    setFormData({
      fj130_21: user?.userId || '',
      fj130_80: 'N',
    });
    setDetailTables(emptyDetails());
  }, [user]);

  return {
    record, formData, detailTables, loading,
    loadRecord, setField, getField,
    setDetailTable,
    handleSave, handleDelete,
    revertToRecord, resetForAdd, applyResult,
  };
}
